import { createFeatureSelector, createSelector } from "@ngrx/store";
import { isToday, isWithinNumDays } from "src/app/shared/functions/date-ranges";
import { NotificationState } from "./notification.reducer";

export const selectNotificationState = createFeatureSelector<NotificationState>('notifications');

export const selectAllNotifications = createSelector(
    selectNotificationState,
    (state: NotificationState) => state.notifications
);

export const selectStudentNotificationSettings = createSelector(
    selectNotificationState,
    (state: NotificationState) => state.studentNotificationSettings
);

export const selectSchoolNotificationSettings = createSelector(
    selectNotificationState,
    (state: NotificationState) => state.schoolNotificationSettings
);

export const selectUnreadNotificationsCount = createSelector(
    selectNotificationState,
    (state: NotificationState) => state.notifications.filter(notification => !notification.read).length
);

export const selectNotificationsFromToday = createSelector(
    selectAllNotifications,
    (notifications) =>
        notifications.filter(notification => isToday(new Date(notification.timestamp)))
);

export const selectNotificationsFromThisMonth = createSelector(
    selectAllNotifications,
    (notifications) =>
        notifications
            .filter(notification => isWithinNumDays(new Date(notification.timestamp), 30)) // Filter notifications from the last 30 days
            .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()) // Sort by timestamp, latest to oldest
);

export const selectStudentNotificationSettingsById = (studentId: string) =>
    createSelector(
        selectStudentNotificationSettings,
        (studentSettings) => studentSettings[studentId]
    );

export const selectSchoolNotificationSettingsById = (schoolId: string) =>
    createSelector(
        selectSchoolNotificationSettings,
        (schoolSettings) => schoolSettings[schoolId]
    );

export const selectFilteredNotificationsFromThisMonth = createSelector(
    selectNotificationsFromThisMonth,
    selectStudentNotificationSettings,
    selectSchoolNotificationSettings,
    (notifications, studentSettings, schoolSettings) => {
        return notifications.filter(notification => {

            if (!notification.relatedIds) {
                return false;
            }

            const relatedStudentId = notification.relatedIds.find(rel => rel.name === 'student')?.identifier;

            const relatedSchoolId = notification.relatedIds.find(rel => rel.name === 'school')?.identifier;

            const studentSetting = relatedStudentId ? studentSettings[relatedStudentId] : undefined;

            const schoolSetting = relatedSchoolId ? schoolSettings[relatedSchoolId] : undefined;

            // Determine whether the notification should be filtered based on the icon
            switch (notification.icon) {
                case 'report':
                    return studentSetting?.reportCards;
                case 'checklist-apple':
                    return studentSetting?.homework; // Assuming you want this by default
                case 'group-achievement':
                    return studentSetting?.marks;
                case 'schedule':
                    return studentSetting?.timetables;
                case 'podium':
                    return studentSetting?.matchResults; // Assuming sportsResults is in studentSetting
                case 'bus':
                    return studentSetting?.travelAlert;
                case 'megaphone':
                    return schoolSetting?.announcements;
                case 'email':
                    return studentSetting?.email;
                default:
                    return true; // Return true if the icon doesn't match any specific setting
            }
        });
    }
);